import type { FormControls } from '@cfgtech/ui'
import type { AdvancedInfoFormSchema } from '~/components/issue/details/AdvancedInfoForm/AdvancedInfoForm.types'
import type { BaseInfoFormSchema } from '~/components/issue/details/BaseInfoForm/BaseInfoForm.types'

export default defineI18nLocale(() => ({
  openingHours: {
    working: {
      asap: {
        title: 'do hodiny',
      },
    },

    weekend: {
      title: 'následující pracovní den',
    },
  },

  issueDetail: {
    meta: {
      title: 'Dluhopis {name}',
    },

    back: 'Zpět na seznam',

    preview: 'Zobrazit náhled emise',

    error: {
      title: '<b>Při odesílání formuláře došlo k chybě.</b> Na jejím řešení aktivně pracujeme.',
      description: 'Kontaktujte nás a vyřešíme Váš požadavek společně.',
    },

    collaborationPlans: {
      Dluhopisomat: 'Premium',
      SrovnavacPremium: 'Premium',
      SrovnavacFreemium: 'Starter Promo',
      Free: 'Zdarma',
      null: 'Zdarma',
    },

    benefits: {
      top: {
        title: 'Topování',
        tooltip: '',
      },
      leads: {
        title: 'Sběr leadů investorů',
        tooltip: '',
      },
      ppc: {
        title: 'PPC kampaně',
        tooltip: '',
      },
    },

    success: {
      title: 'Doplnění emise proběhlo úspěšně',
      description: 'Změna bude zveřejněna na profilu Vaší emise do 5 minut',
    },

    tariffs: {
      premium: {
        name: 'Premium',
      },
      free: {
        name: 'Zdarma',
      },
    },
    sections: {
      image: {
        title: 'Obrázek',
        subtitle: 'Vhodná fotografie nebo video výstižně <strong>dokreslí důvody, proč do Vašeho dluhopisu investovat.</strong>',
        instructions: '<span class="flex flex-wrap justify-center gap-x-1.5"><span>Doporučený rozměr <b>1360x558 px</b>.</span> <span>Formát: <b>{formats}</b></span></span>',
        actions: {
          delete: 'Vymazat obrázek',
          uploadDifferent: 'Nahrát jiný obrázek',
          add: 'Doplnit obrázek',
        },
        status: {
          success: {
            delete: {
              title: 'Obrázek byl úspěšně odstraněn',
            },
            upload: {
              title: 'Obrázek byl úspěšně nahrán',
            },
          },
        },
        deleteModal: {
          title: 'Chcete obrázek opravdu odstranit?',
          description: 'Tuto akci nelze vrátit zpět.',
          actions: {
            delete: 'Odstranit',
            cancel: 'Zrušit',
          },
        },
      },
      topping: {
        title: 'Topovat emisi',
      },

      baseInfo: {
        title: 'Základní údaje',

        submit: {
          label: 'Uložit změny',
        },

        fields: {
          yieldType: {
            placeholder: 'Typ výnosu dluhopisu',
          },
          interestRate: {
            placeholder: 'Úroková míra',
            stockPlaceholder: 'Obchodní úroková míra',
          },
          totalAmount: {
            placeholder: 'Objem emise',
          },
          availableCount: {
            placeholder: 'Počet kusů k prodeji',
          },
          nominalValue: {
            placeholder: 'Jmenovitá hodnota',
          },
          maturityDate: {
            placeholder: 'Datum splatnosti',
          },
          hasEarlyRepayment: {
            label: 'Možnost podání žádosti investorem o předčasné splacení',
          },
          returnPeriodicity: {
            placeholder: 'Periodicita výplat',
          },
          isPerpetual: {
            label: 'Je perpetuitní',
          },
        } satisfies Record<keyof FormControls<BaseInfoFormSchema>, {
          placeholder?: string
          caption?: string
          label?: string
          [key: string]: string | undefined
        }>,
      },

      advancedInfo: {
        title: 'Souhrn informací o dluhopisu',
        fields: {
          issueDate: {
            placeholder: 'Datum emise',
          },

          interestStartDate: {
            placeholder: 'Datum začátku úročení',
          },

          bondResemblance: {
            placeholder: 'Podoba dluhopisu',
          },

          bondForm: {
            placeholder: 'Forma dluhopisu',
          },
        } satisfies Record<keyof FormControls<AdvancedInfoFormSchema>, {
          placeholder?: string
          caption?: string
          label?: string
        }>,

        submit: {
          label: 'Uložit změny',
        },
      },

      motivation: {
        title: 'Důvody pro zakoupení investice',
        subtitle: 'Výstižný popis důvodů, proč by potenciální investoři měli investovat do Vašeho dluhopisu jim <b>umožní rychlé vnímání přínosů a také porovnání s ostatními dluhopisy.</b> Uveďte silné stránky své společnosti, její úspěchy a výsledky a další výhody své emise.',
        addInformationModal: {
          title: 'Důvody pro zakoupení investice',
        },
      },

      moneyUsagePlan: {
        title: 'Plánované využití peněz',
        subtitle: 'Potenciální investory zajímá, <b>jak využijete získané finanční prostředky</b>, které Vám poskytnou. Čím přesnější a popisnější budete, tím lépe.',
        addInformationModal: {
          title: 'Plánované využití peněz',
        },
      },

      collaterals: {
        title: 'Zajištění',
        subtitle: 'Popis forem zajištění je jeden z <b>hlavních rozhodovacích faktorů</b>, které potenciální investoři hledají a zvažují. Pokud je Váš dluhopis zajištěn, popište podrobně jednotlivá zajištění.',
        addInformationModal: {
          title: 'Zajištění',
        },
      },

      returns: {
        title: 'Přehled výplaty úroků',
        subtitle: 'Pravidelné aktualizování stavu jednotlivých výplat výnosů umožní investorům mít <b>lepší přehled o jejich průběhu u všech Vašich emisí.</b>',
        addInformationModal: {
          title: 'Přehled výplaty úroků',
        },
      },

      documents: {
        title: 'Dokumenty',
        subtitle: 'Jelikož je <b>pro potenciální investory důležité ověřit si informace</b> uváděné na profilu Vaší emise, velmi pomůže umožnit jim nahlížet do všech důležitých dokumentů, které se vážou k Vaší emisi a společnosti.',
        addInformationModal: {
          title: 'Dokumenty společnosti',
        },
        company: {
          title: 'Dokumenty společnosti {companyName}',
        },
      },

    },
  },

  companyDetail: {
    back: 'Zpět na seznam',
    preview: 'Zobrazit náhled',
    meta: {
      title: 'Společnost {name}',
    },

    error: {
      title: '<b>Při odesílání formuláře došlo k chybě.</b> Na jejím řešení aktivně pracujeme.',
      description: 'Kontaktujte nás a vyřešíme Váš požadavek společně.',
    },

    success: {
      title: 'Doplnění společnosti proběhlo úspěšně',
      description: 'Změna bude zveřejněna na profilu Vaší společnosti do 5 minut',
    },

    sections: {
      hero: {
        companyId: 'IČO: {companyId}',
      },
      baseInfo: {
        title: 'Základní údaje',
        fields: {
          businessCategory: {
            placeholder: 'Obor podnikání',
            description: 'Obor podnikání se zobrazí a pomůže filtrování na <a href="https://srovnavacdluhopisu.cz/#issues-list" class="text-brand underline hover:no-underline">seznamu dluhopisů</a>',
          },
          legalForm: {
            placeholder: 'Právní forma',
            description: 'Např. Společnost s ručením omezeným',
          },
          startDate: {
            placeholder: 'Datum založení společnosti',
          },
          endDate: {
            placeholder: 'Datum zániku společnosti',
            description: 'Zadejte, pokud společnost je zaniklá',
          },
          businessTypeByCzNace: {
            placeholder: 'Typ podnikání dle CZ NACE',
          },
          fullAddress: {
            placeholder: 'Adresa sídla',
          },
        },
      },
      aboutCompany: {
        title: 'Text “O společnosti”',
        subtitle: 'Text “O společnosti“ pomáhá seznámit investory s Vaší společností, ukázat její silné stránky a výjimečnost mezi ostatními.',
      },
      issuerMessages: {
        title: 'Zprávy od emitenta',
        subtitle: 'Zprávy od emitenta <b>pomáhají upevňovat vztahy</b> s investory a zájemci o investici tím, že jim pravidelně <b>poskytnete aktuální a důležité informace</b> o aktuálním stavu emise a činnostech společnosti.',
      },
      financialResults: {
        title: 'Finanční výsledky',
        subtitle: `Kompletně vyplněné finanční výsledky jsou pro potenciální investory dalším důležitým rozhodovacím kritériem. Zároveň mohou vylepšit skóre rizikovosti počítané podle @:common.mfcrLink{'.'}`,
      },
      ownerStructure: {
        title: 'Vlastnická struktura',
      },
    },
  },

  freeModal: {
    description:
    {
      text: 'Po kliknutí na tlačítko níže Vám zavolá náš manažer podpory emitentů, který Vám spolu s copywritingovým specialistou pomůže sepsat veškerou textaci pro prezentaci Vaší emise.',
      nonText: 'Po kliknutí na tlačítko níže Vám zavolá náš manažer podpory emitentů, který Vám pomůže s každým krokem doplnění informací o Vašem dluhopisu.',
    },

    contact: {
      callMe: 'Nechat si zavolat',
    },
  },

  premiumModal: {
    description: 'Odemkněte pokročilé marketingové sekce své emise s tarifem Premium a umožněte tak desetitisícům návštěvníků webu atraktivněji vnímat Vaši emisi - <b>zvýšíte návštěvnost, důvěryhodnost i konverze.</b>',

    contact: {
      title: 'Zavolám Vám {term}, odpovím na všechny otázky a společně dokončíme Vaši žádost o změnu.',
      pricing: 'Kompletní ceník',
      callMe: 'Nechat si zavolat',
    },

    // Texts for specific fields
    titles: {
      'Topovat emisi': 'Premium',
    },
    descriptions: {
      'Topovat emisi': {
        top: 'Vaše emise budou automaticky řazeny mezi těmi nejvýše umístěnými v seznamu dluhopisů.<br><br><b>S tarifem Premium také:</b>',
        bottom: 'Odemknete pokročilé marketingové sekce své emise, budete moct zveřejnit odkaz na své stránky a umožníte tak desetitisícům návštěvníků webu atraktivněji vnímat svou emisi – zvýšíte návštěvnost, důvěryhodnost i zájem.',
      },
    },
  },

  uploadFileModal: {
    caption: 'Rok, za který je daný dokument platný',
    description: 'Např. rozvaha za 2023, nebo ke kterému byl daný dokument podepsán (např. emisní podmínky platné k 2023).',

    document: {
      title: 'dokument',
      formats: 'Formát: .doc, .docx, .pdf',
    },

    action: {
      upload: 'Nahrát dokument',
      uploadAnotherDocument: 'Nahrát jiný dokument',
      save: 'Uložit',
    },
  },

  card: {
    getPremium: 'Získat premium',

    issue: {
      manage: 'Upravit emisi',
    },
    company: {
      manage: 'Upravit společnost',
    },

  },

  documents: {
    noData: 'Žádné dokumenty nebyly nalezeny',
    action: {
      upload: 'Nahrát dokument',
    },
  },

  fileCategories: {
    issue: {
      termSheet: 'Emisní podmínky',
      issueTermSheet: 'Emisní podmínky',
      amendmentToTermSheet: 'Emisní dodatek',
      collateralInstruments: 'Zajišťovací instrumenty',
      prospectusAmendment: 'Dodatek k prospektu',
      otherDocuments: 'Další dokumenty emisí',
    },
    company: {
      balanceSheet: 'Rozvaha',
      baseProspectus: 'Základní prospekt',
      auditReport: 'Zpráva auditora',
      annualReport: 'Výroční zpráva',
      incomeStatement: 'Výkaz zisku a ztráty',
      financialStatement: 'Účetní závěrka',
      cashFlowStatement: 'Přehled o peněžních tocích',
      otherDocuments: 'Další dokumenty společnosti',
    },
  },

  returnCalendar: {
    hint: {
      future: 'Toto úrokové období ještě neproběhlo. Pokud investujete, můžete být i Vy jeho součástí.',
      paid: 'Úroky za toto období byly investorům úspěšně vyplaceny.',
      pending: 'Úroky za toto období jsou aktuálně zpracovávány. Potvrzení o proběhlé výplatě se zde zobrazí během několika dní.',
      unknown: 'Nemáme dostatečné informace o této výplatě úroku.',
      unpaid: 'Úroky za toto období zatím nebyly vyplaceny.',
    },
    paymentFor: 'Výplata za {periodicity} {year}',
  },

  verification: {
    meta: {
      title: 'Verifikace',
    },

    title: 'Pracujeme na verifikaci Vašeho přístupu ke spravování emisí',
    description: 'Zavoláme Vám {term} a verifikaci společně dokončíme',

    contact: {
      title: 'Pokud máte jakékoliv otázky neváhejte se na mě obrátit',
      callme: 'Nechte si zdarma zavolat zpět',
    },
  },

  contact: {
    managerName: 'Jan',
    manager: {
      telephone: 'Telefon:',
      openningHours: '(Po-Pá: 9-17 hod)',
      email: 'E-mail:',
    },

    leadDialog: {
      titleNextWorkingDay: 'Zanechte nám kontakt a @:contact.managerName Vám zavolá následující pracovní den',
      titleCallNow: 'Zanechte nám kontakt a @:contact.managerName Vám do hodiny zavolá zpět',
      description: 'Odpovím na Vaše otázky, vysvětlím, která kritéria jsou při investování důležitá, nebo Vám pomůžu s nákupem dluhopisu.',
      success: {
        title: 'Úspěšně jsme přijali Váš požadavek.',
        description: 'Naše zákaznická podpora Vás bude kontaktovat co nejdříve.',
      },
      title: 'Neváhejte a napište nám',
      descriptionContact: 'V pracovních dnech se Vám ozveme do 1 hodiny',
      form: {
        email: 'E-mail',
        phone: 'Telefon',
        confirm: 'Odeslat',
        noteNotFilled: 'Nevyplněno',
      },
    },
  },

  nav: {
    addBonds: 'Přidat dluhopis',
    getInterestedOfBond: 'Reklama a leady',
    blog: 'Blog',
    changeBonds: 'Změnit dluhopis',
    riskSkoring: 'Skóre dle MF ČR',
    home: 'Seznam dluhopisů',
    companyList: 'Seznam emitentů',
    forIssuers: 'Pro emitenty',
    aboutUs: 'O nás',
    contact: 'Kontakt',

    topAriaLabel: 'Navigace v hlavičce stránky',
    footerAriaLabel: 'Navigace v patičce stránky',

    forCompanies: {
      overview: 'Přehled',
      offerings: 'Nabídka služeb',
      consultation: 'Poradna',
      caseStudies: 'Případová studie',
      pricing: 'Ceník',

      issues: 'Emise',
      wantToRelease: 'Chci vydat',
      haveReleased: 'Vydal jsem',
      haveSubscribed: 'Mám upsáno',

      myAccount: 'Můj účet',
      manageIssue: 'Spravujte svou emisi',
      login: 'Přihlásit se',
      register: 'Registrovat se',
      logout: 'Odhlásit se',

      contactUs: {
        title: 'Kontaktujte nás',
        time: 'Pracovní dny 9-17 hod',
      },
    },
  },

  common: {
    more: 'zobrazit více',
    less: 'zobrazit méně',
    footerDisclaimer:
            'Provozovatelem Srovnávače je společnost Klub investorů a traderů s.r.o., IČO 24161179, sídlem Vinohradská 2396/184, 130 00, Praha 3 (dále jen „Společnost“). Společnost v rámci Srovnávače uveřejňuje a srovnává informace o dluhopisech, které jsou veřejně dostupné nebo které emitenti těchto dluhopisů sami Společnosti sdělí. Uveřejnění dluhopisu emitenta na Srovnávači nelze v žádném případě vykládat jako investiční doporučení ke koupi dluhopisu, ani jakékoliv hodnocením finančního stavu emitenta, predikcí budoucího vývoje činnosti emitenta či hodnocení vhodnosti či výhodnosti investice pro konkrétního zájemce o dluhopis. Společnost důrazně doporučuje uživatelům, aby informace o emitentech a dluhopisech považovali pouze za informativní a aby veškeré inzerované emitenty a dluhopisy důkladně prověřili, např. za asistence nezávislých odborných investičních, ekonomických či právních poradců. Společnost nenese odpovědnost za jejich správnost, přesnost, úplnost a zákonnost. Na základě objektivních informací Společnost přiděluje jednotlivým dluhopisům skóre dle Scorecardu korporátních dluhopisů 2.0 vydaného Ministerstvem financí České republiky. Dle této metodiky je na základě dosaženého skóre dluhopis zařazen do rizikové kategorie dle Scorecardu korporátních dluhopisů 2.0 vydaného Ministerstvem financí České republiky. Společnost postupuje při vyhodnocení skóre dle Scorecardu korporátních dluhopisů 2.0 vydaného Ministerstvem financí České republiky s maximální možnou péči, přesto však neodpovídá za výsledek provedeného hodnocení. Výsledek provedeného hodnocení není v žádném případě investičním doporučením ve smyslu zákona č. 256/2004 Sb., o podnikání na kapitálovém trhu (dále jen „ZPKT“). Metodika dle Scorecardu korporátních dluhopisů 2.0 vydaného Ministerstvem financí České republiky není závazným právním předpisem a Společnost není ratingovou agenturou. Společnost pouze dle této metodiky vyhodnocuje veřejně dostupné dluhopisy a usnadňuje orientaci uživatelům Srovnávače. Společnost tak upozorňuje všechny uživatele, že neposkytuje investiční službu přijímání a předávání pokynů týkajících se investičních nástrojů, a to ani zprostředkování této investiční služby, investiční poradenství týkající se investičních nástrojů či umisťování investičních nástrojů bez závazku jejich upsání, ani žádnou jinou investiční službu ve smyslu ZPKT a nezastává ani žádnou další roli dle tohoto zákona (burza cenných papírů, organizátor regulovaného trhu, provozovatel mnohostranného obchodního systému apod.). Každý uživatel Srovnávače je povinen se seznámit s podmínkami užívání Srovnávače. Každý investor do korporátních dluhopisů si musí být vědom toho, že investice do dluhopisů sebou nese též riziko ztráty investovaných prostředků a riziko nedosažení zisku. Jakýkoliv historický výnos není zárukou výnosů budoucích, ty závisí především na budoucím vývoji emitenta a jeho podnikání. V souvislosti s tímto rizikem Společnost doporučuje, aby se každý uživatel Srovnávače seznámil se sdělením České národní banky, které je určeno pro investory do korporátních dluhopisů:',
    yes: 'Ano',
    undefined: 'Neznámo',
    no: 'Ne',
    true: 'Ano',
    false: 'Ne',
    details: 'Detail',
    goodDay: 'Dobrý den',
    unavailable: 'není k dispozici',
    download: 'Stáhnout',
    add: 'Doplnit',

    close: 'Zavřít',

    years: 'let',

    zoom: 'Zvětšit',

    null: '-',

    delete: 'Odstranit',
    cancel: 'Zrušit',
    confirm: 'Potvrdit',

    mfcrLink: '<a href=\"https://srovnavacdluhopisu.cz/skore-rizikovosti\">metodiky MF ČR</a>',

    numberAbbreviation: {
      thousand: 'tis',
      million: 'mil',
      billion: 'mld',
      trillion: 'bil',
      e: 'e',
    },

    currencies: {
      CZK: 'Kč',
      EUR: '€',
    },

    date: {
      from: 'od {from}',
      to: 'do {to}',
    },

    motto: 'První a největší <b>srovnávač dluhopisů</b> na trhu',

    notFound: {
      pageTitle: 'Stránka nebyla nalezena',
      title: 'Je nám líto, ale požadovaná <b>stránka nebyla nalezena.</b>',
      description:
        'Ujistěte se, že jste neudělali chybu v URL adrese.<br> Je možné, že byla stránka přemístěna nebo odstraněna.',
      buttonLabel: 'Domovská stránka Sekce pro emitenty',
      motto: 'Všechny Vaše dluhopisy na jednom místě',
    },

    newsletter: {
      aboutUs: {
        boast: 'největší srovnávač dluhopisů v ČR',
        title: 'Srovnávač dluhopisů<br/>patří do skupiny CFG.',
        preDescription: '10 let na finančním trhu v ČR',
        description: 'Investiční skupina CFG se pohybuje na trhu už více než 10 let. Díky své odbornosti a bohatým zkušenostem poskytujeme kvalitní servis partnerům, investorům, klientům a také emitentům. Společně měníme svět financí.',
      },
      form: {
        title: 'Buďte mezi prvními,<br/>kdo se dozví o novinkách na srovnávači.',
        description: 'Zanechte nám svoje kontakty, ať nezmeškáte jedinou příležitost',
        fields: {
          email: {
            label: 'E-mail',
            labelShort: 'Váš email',
          },
          name: {
            label: 'Vaše jméno',
          },
          confirm: {
            label: 'Souhlasím se <a class="text-grey-400 font-bold underline" href="https://srovnavacdluhopisu.cz/informace-o-zpracovani-osobnich-udaju/" target="_blank">zpracováním osobních údajů</a>.',
          },
          tel: {
            label: 'Telefon',
          },
        },
        optional: '(nepovinné)',
      },
      cta: 'Odeslat',
      confirm: 'Potvrďte, prosím, přihlášení do newsletteru ve Vašem e&#8209;mailu. Prohlédněte i složku hromadné, promo akce nebo spam.',
      gdprError: 'Je třeba zaškrtnout pole',
      small: {
        title: 'Novinky ze světa dluhopisů',
        description: 'Přihlaste se k našemu newsletteru a získávejte užitečné informace od ekonoma a finančního analytika <br/><strong>Ing. Vladimíra Pikory, Ph.D.</strong>',
      },
    },
    seo: {
      title: 'Největší srovnávač dluhopisů',
      description: 'Porovnejte si stovky dluhopisů podle různých rozhodovacích kritérií, která jsou pro vás důležitá. Vyzkoušejte nejkomplexnější srovnávač dluhopisů na trhu.',
      slugDescription: 'Porovnejte dluhopis {issueName} s ostatními dluhopisy na trhu podle různých kritérií včetně výnosu, zajištění a rizikovosti.',
      imageAltHomePage: 'Srovnávač dluhopisů',
      imageAltSlug: 'Dluhopis {name} na srovnávači dluhopisů',
    },
    validators: {
      email: 'Zadejte platnou e-mailovou adresu',
      maximumDecimals: 'Zadejte číslo s max. {n} desetinnými místy',
      integer: 'Zadejte celé číslo',
      positiveNumber: 'Zadejte kladné číslo',
      required: 'Pole je povinné',
      percentage: 'Zadejte číslo v rozsahu 0 až 100',
      ico: 'Zadejte platné IČO',
      bankAccount: 'Zadejte platný bankovní účet',
      phone: 'Zadejte platné telefonní číslo',
      isin: 'Zadejte platný ISIN',
      birthNumber: 'Zadejte platné rodné číslo',
      gdprConfirm: 'Je třeba zaškrtnout pole',
      number: 'Zadejte číslo',
      availableOwnershipAmount: 'Vlastníte pouze {amount} ks dluhopisů.',
      biggerThanZero: 'Zadejte číslo větší než 0',
      password: {
        invalid: 'Heslo nesplňuje všechny podmínky formátu.',
        minChars: '8 znaků',
        upperAndLowerCase: '1 velké a 1 malé písmeno',
        number: '1 číslo [0-9]',
        symbol: '1 symbol (např. &#63; &#33; &#37; &#64; &#35; &#36; &#38; &#43; &#42;)',
      },
    },
  },

  issuesList: {
    meta: {
      title: 'Vaše dluhopisy',
    },
    header: {
      title: ' Spravujte prezentaci svých společností a emisí na Srovnávači',
      subtitle: 'Vítejte v rozhraní pro emitenty Srovnávače dluhopisů',
    },
    title: 'Vaše emise a společnosti',
    empty: {
      title: 'Aktualně u Vás neevidujeme žádné dluhopisy ve správě.',
      emptyCompany: 'Momentálně u této společnosti neevidujeme žádné dluhopisy ve správě.',
      contact: {
        title: 'Kontaktujte mě a doplníme emise společně',
        button: {
          label: 'Nechat si zavolat',
        },
      },
    },
  },

  stepper: {
    stepsOf: 'Krok {current} z {total}',
  },

  registration: {
    stepper: {
      email: 'Email',
      password: 'Heslo',
      phone: 'Ověření',
    },
    error: {
      emailAlreadyExists: 'Tento e-mail již používá jiný uživatel',
      generic: 'Nastala chyba, kontaktujte podporu',
    },
    form: {
      email: 'E-mail',
      password: 'Heslo',
      phone: 'Telefonní číslo',
      gdpr: 'Souhlasím se <a class="text-brand underline hover:no-underline" href="https://srovnavacdluhopisu.cz/informace-o-zpracovani-osobnich-udaju/" target="_blank">zpracováním osobních údajů</a>.',
      continue: 'Pokračovat',
      validations: {
        password: {
          mustInclude: 'Musí obsahovat alespoň:',
        },
      },
    },
    contactCard: {
      title: 'Potřebujete více informací o Srovnávači a jeho fungování?',
      callBack: 'Nechte si zdarma zavolat zpět',
      callTime: {
        title: 'Zavolám Vám',
        nextDay: 'následující pracovní den',
        nextHour: 'nejpozději do 1 hodiny',
      },
    },
    steps: {
      email: {
        pageTitle: 'Zaregistrujte se',
        title: 'Upravujte profily Vašich emisí na Srovnávači',
        subTitle: 'Registrujte se zdarma',
        description: 'Vyplněním klíčových údajů, na základě kterých se investoři rozhodují, <strong>zvýšíte zájem o Vaší emisi</strong>',
        alreadyHaveAccount: 'Už máte svůj účet?',
        login: 'Přihlásit se',
      },
      password: {
        pageTitle: `Zaregistrujte se {'|'} Heslo`,
        title: 'Vytvořte si silné heslo',
        wrongEmail: 'Zadali jste špatný e-mail?',
        backToEmail: 'Zpět na e-mail',
      },
      phone: {
        pageTitle: `Zaregistrujte se {'|'} Verifikace`,
        title: 'Prosím, zadejte telefonní číslo pro ověření',
        callTime: {
          text: 'Vám zavoláme, abychom ověřili Vaši totožnost a umožnili Vám spravovat Vaše emise.',
          nextDay: 'Následující pracovní den',
          nextHour: 'Do hodiny',
        },
        finish: 'Dokončit registraci',
      },
    },
  },
  czNaceBusinessTypes: {
    A: 'A - Zemědělství, lesnictví, rybářství',
    B: 'B - Těžba a dobývání',
    C: 'C - Zpracovatelský průmysl',
    D: 'D - Výroba a rozvod elektřiny, plynu, tepla a klimatiz. vzduchu',
    E: 'E - Zásobování vodou; činnosti související s odpady a sanacemi',
    F: 'F - Stavebnictví',
    G: 'G - Velkoobchod a maloobchod; opravy a údržba motorových vozidel',
    H: 'H - Doprava a skladování',
    I: 'I - Ubytování, stravování a pohostinství',
    J: 'J - Informační a komunikační činnosti',
    K: 'K - Peněžnictví a pojišťovnictví',
    L: 'L - Činnosti v oblasti nemovitostí',
    M: 'M - Profesní, vědecké a technické činnosti',
    N: 'N - Administrativní a podpůrné činnosti',
    O: 'O - Veřejná správa a obrana; povinné sociální zabezpečení',
    P: 'P - Vzdělávání',
    Q: 'Q - Zdravotní a sociální péče',
    R: 'R - Kulturní, zábavní a rekreační činnosti',
    S: 'S - Ostatní činnosti',
    T: 'T - Činnosti domácností',
    U: 'U - Činnosti exteritoriálních organizací a orgánů',
  },

  issueList: {
    conceptExplanation: {
      title: '5 tipů, jak efektivně propagovat Váš dluhopis na Srovnávači',
      readMore: 'Číst více',
      text: `<li><b class="font-semibold">Doplňte klíčové údaje</b>, na základě kterých se investoři rozhodují.</li>
        <li><b class="font-semibold">Umístěte Vaše emise na přední místa</b> v seznamu dluhopisů díky topování.</li>
        <li><b class="font-semibold">Přesvědčte investory</b> pomocí důvodů k zakoupení, plánovaného využití získané investice a prezentačního videa na detailu Vaší emise.</li>
        <li><b class="font-semibold">Komunikujte s investory</b> pomocí “'Zpráv od emitenta”.</li>
        <li><b class="font-semibold">Oslovujte potenciální investory</b>, ať jsou kdekoliv, pomocí PPC kampaně.</li>`,
      action: 'Dozvědět se více',
    },
  },

  company: {
    economicResults: {
      title: 'Finanční výsledky',

      preview: {
        showMore: 'Zobrazit další ukazatele',
      },

      summary: {
        keyIndicators: '',
        yoy: 'Meziročně',
        revenues: 'Tržby',
        operatingResult: 'Výsledek hospodaření <span class="!font-normal">(před zdaněním)</span>',
        assets: 'Aktiva',
        equity: 'Vlastní kapitál',
        debt: 'Zadluženost',
      },

      fields: {
        longtermAssets: 'Dlouhodobý majetek',
        currentAssets: 'Krátkodobý majetek',
        otherTimeresolvedAssets: 'Ostatní časově rozlišitelná aktiva',
        liabilities: 'Závazky',
        services: 'Služby',
        revenues: 'Tržby',
        expenses: 'Náklady',
        equity: 'Vlastní kapitál',
        profit: 'Zisk',
        otherLiabilities: 'Ostatní časově rozlišitelná pasiva',
        performance: 'Výkon',
        costs: 'Náklady',
        foreignResources: 'Cizí zdroje',
        debt: 'Dluh',
      },

      chartLoading: 'Načítání grafu…',
      noDataAvailable: {
        title: 'Bohužel informace o finančních výsledcích nemáme k dispozici.',
        button: {
          label: 'Doplnit finanční výsledky',
        },
      },

      detail: {
        detailedOverviews: {
          title: 'Detailní přehledy',

          sales: {
            title: 'Tržby',
            description: 'Tržba je v účetnictví a finančním účetnictví souhrn finančních prostředků přijatých určitým podnikatelem nebo v určité provozovně za prodané zboží nebo poskytnuté služby v příslušném časovém období (například denní tržba, týdenní tržba nebo měsíční tržba). Změny tržeb v ekonomice jsou důležitým indikátorem v hodnocení jejího vývoje. Na rozdíl od obratu zahrnuje tržba i platby, které byly později vráceny.',
          },

          profit: {
            title: 'Výsledek hospodaření <span class="!font-normal">(před zdaněním)</span>',
            description: 'Výsledek hospodaření je v ekonomice a účetnictví název pro rozdíl mezi úroky a náklady podniku a představuje tak zisk nebo ztrátu podniku za určité období nebo dobu.',
          },

          assets: {
            title: 'Aktiva',
            description: 'Aktiva představují v účetnictví majetek – zásoby, licence, peníze. Jedná se o majetek, který účetní jednotka získala nebo získá a přináší výnos. Existují dvě rozdělení aktiv – reálná a finanční. Reálná aktiva jsou např. nemovitosti, pozemky, stroje, ale také patenty nebo obchodní značky. Pod finančními aktivy si můžeme představit peněžní prostředky, dluhopisy a akcie.',
          },

          passives: {
            title: 'Pasiva',
            description: 'Pasiva zahrnují zdroje financování aktiv, která se člení na vlastní a cizí zdroje. Vlastní kapitál zahrnuje finanční zdroje vlastníků společnosti (základní kapitál, kapitálové fondy, zákonné fondy, zadržené zisky minulých let a zisk běžného období). Zatímco cizí zdroje zahrnují celkové závazky, tj. úročené dluhy (úvěry a dluhopisy), závazky z obchodních vztahů, daňové závazky, závazky vůči zaměstnancům atd.',
          },

          equity: {
            title: 'Vlastní kapitál',
            description: 'Vlastní kapitál zahrnuje finanční zdroje vlastníků společnosti (základní kapitál, kapitálové fondy, zákonné fondy, zadržené zisky minulých let a zisk běžného období).',
          },

          debt: {
            title: 'Zadluženost',
            description: 'Ukazatele zadluženosti neboli ukazatele řízení dluhu informují o finanční struktuře společnosti, tj. v jakém poměru jsou aktiva společnosti financovány z vlastních a cizích zdrojů. Rozlišujeme několik ukazatelů zadluženosti – míra celkové zadluženosti, poměr vlastního kapitálu a celkových aktiv, míru zadluženosti vlastního kapitálu a úrokové krytí.',
          },
        },
      },
    },
    ownerships: {
      valuePercent: 'Majetkový podíl:&nbsp;{percent}&nbsp;%',

      maternal: {
        title: 'Vlastníci společnosti',
        description: 'Společnosti nebo fyzické osoby, které mají ve společnosti {companyName} vlastnický podíl (např. mateřské společnosti). Uvádíme pouze vztahy 1. úrovně (tj. přímí vlastníci).',
        empty: {
          title: 'Pro společnost {companyName} neznáme žádného vlastníka.',
        },
      },

      subsidiary: {
        title: 'Společnost vlastní',
        description: 'Společnosti, ve kterých má společnost {companyName} vlastnický podíl (např. dceřinné společnosti). Uvádíme pouze vztahy 1. úrovně (tj. společnosti vlastněné napřímo).',
        empty: {
          title: 'Společnost {companyName} nevlastní žádnou další společnost.',
        },
      },
    },
    statutory: {
      title: 'Statutární orgán',
    },
  },
}))
